import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { initializeApp, deleteApp } from 'firebase/app';
import {
  createUserWithEmailAndPassword,
  getAuth,
  signOut,
  updateProfile,
} from 'firebase/auth';
import { ref, set, update } from 'firebase/database';
import { httpsCallable } from 'firebase/functions';
import firebaseApp, { db, functions } from '../../config/firebase';
import Button from '../ui/Button';
import Modal from '../ui/Modal';
import {
  WorkspaceEmptyState,
  WorkspacePageLoader,
  WorkspaceStatusChip,
} from '../ui/Workspace';
import { useAdminCoursesOverview } from '../../hooks/useLmsData';
import { useAuth } from '../../contexts/AuthContext';

const STAFF_ROLES = ['instructor', 'demonstrator', 'manager'];
const TEACHING_ROLES = ['instructor', 'demonstrator'];

const STAFF_ROLE_OPTIONS = [
  {
    value: 'instructor',
    label: 'Instructor',
    description: 'Teaching workspace, courses, schedule, and assigned learners.',
  },
  {
    value: 'demonstrator',
    label: 'Demonstrator',
    description: 'Class support workspace with teaching access, without management tools.',
  },
  {
    value: 'manager',
    label: 'Manager',
    description: 'Operational and management access, excluding configuration pages.',
  },
];

const normalizeStaffRole = (role) => {
  const normalized = String(role || '').toLowerCase();
  if (normalized === 'staff') return 'demonstrator';
  return normalized;
};

const getStaffRoleLabel = (role) =>
  STAFF_ROLE_OPTIONS.find((option) => option.value === normalizeStaffRole(role))
    ?.label || 'Staff';

const canOwnCourses = (role) => TEACHING_ROLES.includes(normalizeStaffRole(role));

const staffHasLogin = (staff) =>
  Boolean(staff?.authCreated || staff?.authCreatedAt || staff?.authLinkedUid);

const generatePassword = () => {
  const charset =
    'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*';
  let password = '';
  for (let i = 0; i < 12; i += 1) {
    password += charset.charAt(Math.floor(Math.random() * charset.length));
  }
  return password;
};

const InstructorsPage = () => {
  const { currentUser } = useAuth();
  const { data, loading, error, refresh } = useAdminCoursesOverview();
  const [selectedInstructorId, setSelectedInstructorId] = useState('');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isPromoteModalOpen, setIsPromoteModalOpen] = useState(false);
  const [assignmentSelection, setAssignmentSelection] = useState([]);
  const [profileForm, setProfileForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    role: 'instructor',
    status: 'active',
    password: '',
  });
  const lastSyncedStaffIdRef = useRef('');
  const [saveAssignmentsLoading, setSaveAssignmentsLoading] = useState(false);
  const [saveProfileLoading, setSaveProfileLoading] = useState(false);
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [isEditingAssignments, setIsEditingAssignments] = useState(false);
  const [pageError, setPageError] = useState('');

  const users = useMemo(() => data?.users || {}, [data]);
  const students = useMemo(() => data?.students || {}, [data]);
  const courses = useMemo(() => data?.courseList || [], [data]);

  const allRegisteredUsers = useMemo(() => {
    const merged = new Map();

    Object.entries(users).forEach(([id, user]) => {
      merged.set(id, {
        id,
        ...user,
        firstName:
          user.firstName || user.personalDetails?.firstName || '',
        lastName: user.lastName || user.personalDetails?.lastName || '',
        email: user.email || user.personalDetails?.email || '',
        role: normalizeStaffRole(user.role || 'student'),
        status: user.status || user.personalDetails?.status || 'active',
      });
    });

    Object.entries(students).forEach(([id, student]) => {
      const existing = merged.get(id) || {};
      merged.set(id, {
        ...existing,
        id,
        ...student,
        firstName:
          existing.firstName ||
          student.firstName ||
          student.personalDetails?.firstName ||
          '',
        lastName:
          existing.lastName ||
          student.lastName ||
          student.personalDetails?.lastName ||
          '',
        email:
          existing.email || student.email || student.personalDetails?.email || '',
        role: existing.role || student.role || 'student',
        status:
          existing.status ||
          student.status ||
          student.personalDetails?.status ||
          'active',
      });
    });

    return Array.from(merged.values());
  }, [students, users]);

  const instructors = useMemo(
    () =>
      allRegisteredUsers
        .filter((user) => STAFF_ROLES.includes(normalizeStaffRole(user.role)))
        .map((instructor) => {
          const assignedCourses = courses.filter((course) =>
            (course.instructors || []).includes(instructor.id),
          );

          return {
            ...instructor,
            role: normalizeStaffRole(instructor.role),
            assignedCourses,
            coverageStatus: assignedCourses.length ? 'covered' : 'unassigned',
          };
        })
        .sort((a, b) =>
          `${a.firstName || ''} ${a.lastName || ''}`.localeCompare(
            `${b.firstName || ''} ${b.lastName || ''}`,
          ),
        ),
    [allRegisteredUsers, courses],
  );

  const promotableUsers = useMemo(
    () =>
      allRegisteredUsers
        .filter(
          (user) =>
            user.role !== 'admin' &&
            !STAFF_ROLES.includes(normalizeStaffRole(user.role)),
        )
        .sort((a, b) =>
          `${a.firstName || ''} ${a.lastName || ''}`.localeCompare(
            `${b.firstName || ''} ${b.lastName || ''}`,
          ),
        ),
    [allRegisteredUsers],
  );

  const selectedInstructor =
    instructors.find((instructor) => instructor.id === selectedInstructorId) ||
    instructors[0] ||
    null;

  useEffect(() => {
    if (!instructors.length) return;
    if (!instructors.some((instructor) => instructor.id === selectedInstructorId)) {
      setSelectedInstructorId(instructors[0].id);
    }
  }, [instructors, selectedInstructorId]);

  const applyInstructorToForm = useCallback((instructor) => {
    if (!instructor) return;
    setAssignmentSelection(
      courses
        .filter((course) => (course.instructors || []).includes(instructor.id))
        .map((course) => course.id),
    );
    setProfileForm({
      firstName: instructor.firstName || '',
      lastName: instructor.lastName || '',
      email: instructor.email || '',
      phone: instructor.phone || instructor.personalDetails?.phone || '',
      role: normalizeStaffRole(instructor.role || 'instructor'),
      status: instructor.status || 'active',
      password: '',
    });
  }, [courses]);

  useEffect(() => {
    if (!selectedInstructorId) {
      setAssignmentSelection([]);
      setIsEditingProfile(false);
      setIsEditingAssignments(false);
      lastSyncedStaffIdRef.current = '';
      return;
    }

    const instructor = instructors.find((item) => item.id === selectedInstructorId);
    if (!instructor) return;

    const switchedStaff = lastSyncedStaffIdRef.current !== selectedInstructorId;
    if (switchedStaff) {
      lastSyncedStaffIdRef.current = selectedInstructorId;
      applyInstructorToForm(instructor);
      setIsEditingProfile(false);
      setIsEditingAssignments(false);
      return;
    }

    if (!isEditingProfile && !isEditingAssignments) {
      applyInstructorToForm(instructor);
    }
  }, [
    selectedInstructorId,
    instructors,
    isEditingProfile,
    isEditingAssignments,
    applyInstructorToForm,
  ]);

  const stats = {
    total: instructors.length,
    instructors: instructors.filter((item) => item.role === 'instructor').length,
    demonstrators: instructors.filter((item) => item.role === 'demonstrator').length,
    managers: instructors.filter((item) => item.role === 'manager').length,
    assigned: instructors.filter(
      (item) => canOwnCourses(item.role) && item.assignedCourses.length > 0,
    ).length,
    unassigned: instructors.filter(
      (item) => canOwnCourses(item.role) && item.assignedCourses.length === 0,
    ).length,
    uncoveredCourses: courses.filter(
      (course) => !course.instructors || course.instructors.length === 0,
    ).length,
  };

  const handleToggleCourseAssignment = (courseId) => {
    setAssignmentSelection((current) =>
      current.includes(courseId)
        ? current.filter((id) => id !== courseId)
        : [...current, courseId],
    );
  };

  const saveAssignments = async () => {
    if (!selectedInstructor) return;
    if (!canOwnCourses(selectedInstructor.role)) {
      setPageError('Only instructors and demonstrators can be assigned to courses.');
      return;
    }

    setSaveAssignmentsLoading(true);
    setPageError('');

    try {
      const updates = {};

      courses.forEach((course) => {
        const currentAssignments = Array.isArray(course.instructors)
          ? [...course.instructors]
          : [];
        const shouldBeAssigned = assignmentSelection.includes(course.id);
        const isAssigned = currentAssignments.includes(selectedInstructor.id);

        let nextAssignments = currentAssignments;
        if (shouldBeAssigned && !isAssigned) {
          nextAssignments = [...currentAssignments, selectedInstructor.id];
        } else if (!shouldBeAssigned && isAssigned) {
          nextAssignments = currentAssignments.filter(
            (id) => id !== selectedInstructor.id,
          );
        }

        updates[`courses/${course.id}/instructors`] = nextAssignments;
      });

      await update(ref(db), updates);
      setIsEditingAssignments(false);
      refresh();
    } catch (saveError) {
      console.error('Failed to save instructor assignments:', saveError);
      setPageError('Could not save course assignments. Please try again.');
    } finally {
      setSaveAssignmentsLoading(false);
    }
  };

  const saveStaffProfile = async () => {
    if (!selectedInstructor) return;

    setSaveProfileLoading(true);
    setPageError('');

    try {
      const timestamp = new Date().toISOString();
      const nextRole = normalizeStaffRole(profileForm.role);
      const firstName = profileForm.firstName.trim();
      const lastName = profileForm.lastName.trim();
      const email = profileForm.email.trim();
      const phone = profileForm.phone.trim();
      const displayName = `${firstName} ${lastName}`.trim();
      const personalDetails = {
        ...(selectedInstructor.personalDetails || {}),
        firstName,
        lastName,
        email,
        phone,
        status: profileForm.status,
      };

      const updates = {
        [`users/${selectedInstructor.id}/firstName`]: firstName,
        [`users/${selectedInstructor.id}/lastName`]: lastName,
        [`users/${selectedInstructor.id}/email`]: email,
        [`users/${selectedInstructor.id}/phone`]: phone,
        [`users/${selectedInstructor.id}/displayName`]: displayName,
        [`users/${selectedInstructor.id}/role`]: nextRole,
        [`users/${selectedInstructor.id}/status`]: profileForm.status,
        [`users/${selectedInstructor.id}/updatedAt`]: timestamp,
        [`users/${selectedInstructor.id}/personalDetails`]: personalDetails,
      };

      if (canOwnCourses(nextRole)) {
        updates[`instructors/${selectedInstructor.id}`] = {
          ...selectedInstructor,
          firstName,
          lastName,
          email,
          phone,
          displayName,
          role: nextRole,
          status: profileForm.status,
          personalDetails,
          updatedAt: timestamp,
        };
      } else {
        updates[`instructors/${selectedInstructor.id}`] = null;
        courses.forEach((course) => {
          if ((course.instructors || []).includes(selectedInstructor.id)) {
            updates[`courses/${course.id}/instructors`] = course.instructors.filter(
              (id) => id !== selectedInstructor.id,
            );
          }
        });
      }

      if (profileForm.password) {
        if (profileForm.password.length < 8) {
          throw new Error('Password must be at least 8 characters.');
        }
        if (!email) {
          throw new Error('Email is required to create or update login access.');
        }
        if (!currentUser?.uid) {
          throw new Error('You must be signed in to update login credentials.');
        }

        const updateUserPassword = httpsCallable(functions, 'updateUserPassword');
        const authResult = await updateUserPassword({
          uid: selectedInstructor.id,
          newPassword: profileForm.password,
          email,
          adminUid: currentUser.uid,
        });

        if (!authResult.data?.success) {
          throw new Error('Could not update staff login credentials.');
        }

        updates[`users/${selectedInstructor.id}/authCreated`] = true;
        updates[`users/${selectedInstructor.id}/authLinkedUid`] =
          selectedInstructor.id;
        updates[`users/${selectedInstructor.id}/authCreatedAt`] = timestamp;

        if (canOwnCourses(nextRole)) {
          updates[`instructors/${selectedInstructor.id}/authCreated`] = true;
          updates[`instructors/${selectedInstructor.id}/authLinkedUid`] =
            selectedInstructor.id;
        }
      }

      await update(ref(db), updates);

      if (profileForm.status === 'active' || profileForm.status === 'inactive') {
        try {
          const updateUserStatus = httpsCallable(functions, 'updateUserStatus');
          await updateUserStatus({
            uid: selectedInstructor.id,
            disabled: profileForm.status === 'inactive',
          });
        } catch (statusError) {
          console.error('Failed to sync auth account status:', statusError);
        }
      }

      setIsEditingProfile(false);
      setProfileForm((current) => ({ ...current, password: '' }));
      refresh();
    } catch (saveError) {
      console.error('Failed to save staff profile:', saveError);
      setPageError(
        saveError.message || 'Could not save staff profile. Please try again.',
      );
    } finally {
      setSaveProfileLoading(false);
    }
  };

  const cancelProfileEdit = () => {
    setPageError('');
    applyInstructorToForm(selectedInstructor);
    setIsEditingProfile(false);
  };

  const cancelAssignmentEdit = () => {
    setPageError('');
    applyInstructorToForm(selectedInstructor);
    setIsEditingAssignments(false);
  };

  if (loading) return <WorkspacePageLoader />;

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
        {error.message}
      </div>
    );
  }

  return (
    <>
      <div className="mx-auto w-full max-w-[min(100%,96rem)] space-y-3 py-1">

        {/* ── Page header ─────────────────────────────────────────── */}
        <header className="rounded-lg border border-stone-200 bg-white px-4 py-3">
          <div className="flex flex-col gap-3 xl:flex-row xl:items-end xl:justify-between">
            <div className="min-w-0">
              <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-stone-500">
                Workspace
              </p>
              <h1 className="mt-0.5 text-2xl font-semibold tracking-tight text-stone-950">
                Staff Management
              </h1>
            </div>
            <div className="flex flex-wrap items-end gap-2">
              <Button variant="outline" onClick={() => setIsPromoteModalOpen(true)}>
                Upgrade User
              </Button>
              <Button onClick={() => setIsCreateModalOpen(true)}>Add Staff</Button>
            </div>
          </div>

          {/* Stats row */}
          <div className="mt-3 grid grid-cols-2 gap-2 md:grid-cols-4">
            <StaffStat label="Total Staff" value={stats.total} />
            <StaffStat label="Instructors" value={stats.instructors} />
            <StaffStat label="Demonstrators" value={stats.demonstrators} />
            <StaffStat label="Managers" value={stats.managers} />
          </div>

          {/* Staff workspace selector */}
          {instructors.length > 0 && (
            <div className="mt-3 rounded-lg border border-stone-200 bg-white px-3 py-2 text-xs">
              <div className="grid gap-2 md:grid-cols-[minmax(0,1fr)_1fr_1fr_1fr_auto] md:items-end">
                <StaffSummaryItem label="Staff member">
                  <select
                    value={selectedInstructor?.id || ''}
                    onChange={(e) => setSelectedInstructorId(e.target.value)}
                    className="ledger-input min-h-[32px] rounded-md px-2 text-xs"
                  >
                    {instructors.map((i) => (
                      <option key={i.id} value={i.id}>
                        {i.firstName} {i.lastName}
                      </option>
                    ))}
                  </select>
                </StaffSummaryItem>
                {selectedInstructor && (
                  <>
                    <SummaryFact label="Role" value={getStaffRoleLabel(selectedInstructor.role)} />
                    <SummaryFact label="Courses" value={selectedInstructor.assignedCourses.length} />
                    <SummaryFact
                      label="Students"
                      value={selectedInstructor.assignedCourses.reduce(
                        (s, c) => s + (c.activeEnrollmentCount || 0), 0,
                      )}
                    />
                    <WorkspaceStatusChip
                      status={selectedInstructor.status === 'active' ? 'active' : 'inactive'}
                      label={selectedInstructor.status === 'active' ? 'Active' : 'Inactive'}
                    />
                  </>
                )}
              </div>
            </div>
          )}
        </header>

        {/* Error bar */}
        {pageError ? (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {pageError}
          </div>
        ) : null}

        {/* Uncovered courses alert */}
        {stats.uncoveredCourses > 0 && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
            <p className="text-sm font-semibold text-amber-900">
              {stats.uncoveredCourses} course{stats.uncoveredCourses === 1 ? '' : 's'} without teaching staff
            </p>
            <p className="mt-0.5 text-xs text-amber-800">
              Assign instructors below or from the Courses workspace.
            </p>
          </div>
        )}

        {!selectedInstructor ? (
          <WorkspaceEmptyState
            title="No staff members"
            message="Add a staff account or upgrade an existing user to get started."
          />
        ) : (
          <>
            {/* ── Staff profile ──────────────────────────────────── */}
            <section className="overflow-hidden rounded-lg border border-stone-200 bg-white">
              <div className="flex flex-col gap-3 border-b border-stone-200 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex min-w-0 items-center gap-3">
                  <img
                    src={
                      selectedInstructor.photoURL ||
                      `https://ui-avatars.com/api/?name=${selectedInstructor.firstName || 'S'}+${selectedInstructor.lastName || ''}&background=DB2777&color=fff`
                    }
                    alt=""
                    className="h-10 w-10 shrink-0 rounded-full object-cover ring-1 ring-stone-200"
                  />
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <h2 className="text-base font-semibold tracking-tight text-stone-950">
                        {`${selectedInstructor.firstName || ''} ${selectedInstructor.lastName || ''}`.trim() || 'Staff member'}
                      </h2>
                      <WorkspaceStatusChip
                        status={selectedInstructor.role === 'manager' ? 'planning' : selectedInstructor.coverageStatus}
                        label={getStaffRoleLabel(selectedInstructor.role)}
                      />
                    </div>
                    <p className="text-xs text-stone-500">
                      {isEditingProfile ? 'Editing profile' : 'Staff profile and workspace access'}
                    </p>
                  </div>
                </div>
                {isEditingProfile ? (
                  <div className="flex gap-2">
                    <Button type="button" variant="outline" onClick={cancelProfileEdit} disabled={saveProfileLoading}>Cancel</Button>
                    <Button onClick={saveStaffProfile} disabled={saveProfileLoading}>
                      {saveProfileLoading ? 'Saving…' : 'Save changes'}
                    </Button>
                  </div>
                ) : (
                  <Button type="button" variant="outline" onClick={() => setIsEditingProfile(true)}>
                    Edit profile
                  </Button>
                )}
              </div>

              {isEditingProfile ? (
                <div className="grid gap-3 px-4 py-4 md:grid-cols-2">
                  <StaffFormField label="First name" value={profileForm.firstName} onChange={(v) => setProfileForm((c) => ({ ...c, firstName: v }))} required />
                  <StaffFormField label="Last name" value={profileForm.lastName} onChange={(v) => setProfileForm((c) => ({ ...c, lastName: v }))} required />
                  <StaffFormField label="Email" type="email" value={profileForm.email} onChange={(v) => setProfileForm((c) => ({ ...c, email: v }))} required />
                  <StaffFormField label="Phone" type="tel" value={profileForm.phone} onChange={(v) => setProfileForm((c) => ({ ...c, phone: v }))} placeholder="Optional" />
                  <StaffFormSelect
                    label="Role"
                    value={profileForm.role}
                    onChange={(v) => setProfileForm((c) => ({ ...c, role: v }))}
                    options={STAFF_ROLE_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
                    hint={STAFF_ROLE_OPTIONS.find((o) => o.value === profileForm.role)?.description}
                  />
                  <StaffFormSelect
                    label="Status"
                    value={profileForm.status}
                    onChange={(v) => setProfileForm((c) => ({ ...c, status: v }))}
                    options={[{ value: 'active', label: 'Active' }, { value: 'inactive', label: 'Inactive' }]}
                  />
                  <div className="md:col-span-2">
                    <StaffAuthFields
                      hasLogin={staffHasLogin(selectedInstructor)}
                      password={profileForm.password}
                      onPasswordChange={(v) => setProfileForm((c) => ({ ...c, password: v }))}
                      onGeneratePassword={() => setProfileForm((c) => ({ ...c, password: generatePassword() }))}
                    />
                  </div>
                </div>
              ) : (
                <>
                  <div className="grid gap-0 divide-y divide-stone-100 sm:grid-cols-2 sm:divide-x sm:divide-y-0">
                    <div className="grid grid-cols-1 divide-y divide-stone-100 sm:grid-cols-2 sm:[&>*:nth-child(even)]:border-l sm:[&>*:nth-child(even)]:border-stone-100">
                      <StaffDetailItem label="First name" value={selectedInstructor.firstName} />
                      <StaffDetailItem label="Last name" value={selectedInstructor.lastName} />
                      <StaffDetailItem label="Email" value={selectedInstructor.email} />
                      <StaffDetailItem label="Phone" value={selectedInstructor.phone || selectedInstructor.personalDetails?.phone} />
                    </div>
                    <div className="grid grid-cols-1 divide-y divide-stone-100 sm:grid-cols-2 sm:[&>*:nth-child(even)]:border-l sm:[&>*:nth-child(even)]:border-stone-100">
                      <StaffDetailItem label="Role" value={getStaffRoleLabel(selectedInstructor.role)} />
                      <StaffDetailItem label="Status" value={selectedInstructor.status === 'active' ? 'Active' : 'Inactive'} />
                      <StaffDetailItem
                        label="Login access"
                        value={staffHasLogin(selectedInstructor) ? 'Enabled' : 'Not set'}
                        className="sm:col-span-2"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-3 divide-x divide-stone-200 border-t border-stone-200">
                    <StaffSummaryStat label="Assigned courses" value={selectedInstructor.assignedCourses.length} />
                    <StaffSummaryStat
                      label="Active students"
                      value={selectedInstructor.assignedCourses.reduce((s, c) => s + (c.activeEnrollmentCount || 0), 0)}
                    />
                    <StaffSummaryStat label="Account" value={selectedInstructor.status === 'active' ? 'Active' : 'Inactive'} />
                  </div>
                </>
              )}
            </section>

            {/* ── Course assignments ─────────────────────────────── */}
            <section className="rounded-lg border border-stone-200 bg-white">
              <div className="flex flex-col gap-2 border-b border-stone-200 px-3 py-2 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h2 className="text-base font-semibold tracking-tight text-stone-950">Course assignments</h2>
                  <p className="text-xs text-stone-500">
                    {!canOwnCourses(selectedInstructor.role)
                      ? 'Managers use operational access only and are not assigned to courses.'
                      : isEditingAssignments
                      ? 'Choose courses for this staff member, then save.'
                      : 'Courses currently linked to this staff member.'}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <WorkspaceStatusChip
                    status={selectedInstructor.assignedCourses.length ? 'covered' : 'unassigned'}
                    label={`${selectedInstructor.assignedCourses.length} assigned`}
                  />
                  {canOwnCourses(selectedInstructor.role) ? (
                    isEditingAssignments ? (
                      <div className="flex gap-2">
                        <Button type="button" variant="outline" onClick={cancelAssignmentEdit} disabled={saveAssignmentsLoading} size="sm">Cancel</Button>
                        <Button onClick={saveAssignments} disabled={saveAssignmentsLoading} size="sm">
                          {saveAssignmentsLoading ? 'Saving…' : 'Save assignments'}
                        </Button>
                      </div>
                    ) : (
                      <Button type="button" variant="outline" onClick={() => setIsEditingAssignments(true)} size="sm">
                        Assign courses
                      </Button>
                    )
                  ) : null}
                </div>
              </div>

              <div className="p-2">
                {!canOwnCourses(selectedInstructor.role) ? (
                  <WorkspaceEmptyState
                    title="No course assignments for this role"
                    message="Change the role to Instructor or Demonstrator to assign teaching coverage."
                  />
                ) : !courses.length ? (
                  <WorkspaceEmptyState
                    title="No courses found"
                    message="Add courses first, then assign teaching staff here."
                  />
                ) : isEditingAssignments ? (
                  <div className="overflow-x-auto rounded-md border border-stone-200">
                    <table className="min-w-full border-separate border-spacing-0 text-sm">
                      <thead>
                        <tr>
                          {['', 'Course', 'Code', 'Students', 'Instructors', 'Status'].map((col) => (
                            <th key={col} className="border-b border-stone-200 bg-stone-50 px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-wide text-stone-500">{col}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {courses.map((course) => {
                          const isSelected = assignmentSelection.includes(course.id);
                          const instructorCount = (course.instructors || []).length;
                          return (
                            <tr key={course.id} className={`group cursor-pointer ${isSelected ? 'bg-stone-50' : ''}`} onClick={() => handleToggleCourseAssignment(course.id)}>
                              <td className="border-b border-stone-100 px-3 py-2.5 group-hover:bg-stone-50">
                                <input type="checkbox" checked={isSelected} onChange={() => handleToggleCourseAssignment(course.id)} onClick={(e) => e.stopPropagation()} className="h-4 w-4 rounded border-stone-300 text-[#db2777] focus:ring-[#d61f74]" />
                              </td>
                              <td className="border-b border-stone-100 px-3 py-2.5 font-semibold text-stone-950 group-hover:bg-stone-50">{course.name}</td>
                              <td className="border-b border-stone-100 px-3 py-2.5 text-stone-500 group-hover:bg-stone-50">{course.courseCode || '—'}</td>
                              <td className="border-b border-stone-100 px-3 py-2.5 text-stone-700 group-hover:bg-stone-50">{course.activeEnrollmentCount || 0}</td>
                              <td className="border-b border-stone-100 px-3 py-2.5 text-stone-700 group-hover:bg-stone-50">
                                {instructorCount ? `${instructorCount} assigned` : 'None'}
                              </td>
                              <td className="border-b border-stone-100 px-3 py-2.5 group-hover:bg-stone-50">
                                <WorkspaceStatusChip status={isSelected ? 'covered' : 'draft'} label={isSelected ? 'Selected' : 'Open'} />
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : selectedInstructor.assignedCourses.length ? (
                  <div className="overflow-x-auto rounded-md border border-stone-200">
                    <table className="min-w-full border-separate border-spacing-0 text-sm">
                      <thead>
                        <tr>
                          {['Course', 'Code', 'Active students', 'Status'].map((col) => (
                            <th key={col} className="border-b border-stone-200 bg-stone-50 px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-wide text-stone-500">{col}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {selectedInstructor.assignedCourses.map((course) => (
                          <tr key={course.id} className="group">
                            <td className="border-b border-stone-100 px-3 py-2.5 font-semibold text-stone-950 group-hover:bg-stone-50">{course.name}</td>
                            <td className="border-b border-stone-100 px-3 py-2.5 text-stone-500 group-hover:bg-stone-50">{course.courseCode || '—'}</td>
                            <td className="border-b border-stone-100 px-3 py-2.5 text-stone-700 group-hover:bg-stone-50">{course.activeEnrollmentCount || 0}</td>
                            <td className="border-b border-stone-100 px-3 py-2.5 group-hover:bg-stone-50">
                              <WorkspaceStatusChip status="covered" label="Assigned" />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <WorkspaceEmptyState
                    title="No courses assigned"
                    message='Click "Assign courses" to link this staff member to teaching coverage.'
                  />
                )}
              </div>
            </section>
          </>
        )}
      </div>

      <Modal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        panelClassName="inline-block w-full max-w-2xl overflow-hidden rounded-2xl bg-white text-left align-bottom shadow-xl transition-all sm:my-8 sm:align-middle"
      >
        <CreateInstructorModal
          onClose={() => setIsCreateModalOpen(false)}
          onSuccess={() => {
            setIsCreateModalOpen(false);
            refresh();
          }}
        />
      </Modal>

      <Modal
        isOpen={isPromoteModalOpen}
        onClose={() => setIsPromoteModalOpen(false)}
        panelClassName="inline-block w-full max-w-2xl overflow-hidden rounded-2xl bg-white text-left align-bottom shadow-xl transition-all sm:my-8 sm:align-middle"
      >
        <PromoteInstructorModal
          users={promotableUsers}
          onClose={() => setIsPromoteModalOpen(false)}
          onSuccess={() => {
            setIsPromoteModalOpen(false);
            refresh();
          }}
        />
      </Modal>
    </>
  );
};

const CreateInstructorModal = ({ onClose, onSuccess }) => {
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    role: 'instructor',
    status: 'active',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleGeneratePassword = () => {
    setForm((current) => ({ ...current, password: generatePassword() }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError('');

    try {
      const createManagedUser = httpsCallable(functions, 'createManagedUser');
      await createManagedUser({
        firstName: form.firstName,
        lastName: form.lastName,
        email: form.email,
        password: form.password,
        role: form.role,
        status: form.status,
      });
      onSuccess();
    } catch (saveError) {
      const code = saveError?.code || '';
      const message = saveError?.message || '';

      if (
        code.includes('not-found') ||
        code.includes('unimplemented') ||
        message.toLowerCase().includes('function')
      ) {
        try {
          await createInstructorWithSecondaryApp(form);
          onSuccess();
          return;
        } catch (fallbackError) {
          console.error('Fallback instructor creation failed:', fallbackError);
          setError(
            fallbackError.message || 'Could not create staff member.',
          );
          return;
        }
      }

      console.error('Failed to create instructor:', saveError);
      setError(saveError.message || 'Could not create staff member.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6">
      <h2 className="text-xl font-semibold text-stone-950">Add Staff</h2>
      <p className="mt-1 text-sm text-stone-500">
        Create a staff account and choose its dashboard access role.
      </p>

      <form onSubmit={handleSubmit} className="mt-5 space-y-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field
            label="First Name"
            value={form.firstName}
            onChange={(value) => setForm((current) => ({ ...current, firstName: value }))}
          />
          <Field
            label="Last Name"
            value={form.lastName}
            onChange={(value) => setForm((current) => ({ ...current, lastName: value }))}
          />
        </div>
        <Field
          label="Email"
          type="email"
          value={form.email}
          onChange={(value) => setForm((current) => ({ ...current, email: value }))}
        />
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <SelectField
            label="Role"
            value={form.role}
            onChange={(value) => setForm((current) => ({ ...current, role: value }))}
            options={STAFF_ROLE_OPTIONS}
          />
          <SelectField
            label="Status"
            value={form.status}
            onChange={(value) => setForm((current) => ({ ...current, status: value }))}
            options={[
              { value: 'active', label: 'Active' },
              { value: 'inactive', label: 'Inactive' },
            ]}
          />
        </div>
        <div>
          <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-stone-500">Password</label>
          <div className="flex gap-2">
            <input
              type="text"
              value={form.password}
              onChange={(event) =>
                setForm((current) => ({ ...current, password: event.target.value }))
              }
              className="ledger-input min-h-[38px]"
              minLength={8}
              required
            />
            <Button type="button" variant="outline" onClick={handleGeneratePassword}>
              Generate
            </Button>
          </div>
        </div>

        {error ? <div className="text-sm text-red-600">{error}</div> : null}

        <div className="flex justify-end gap-3 pt-2">
          <Button type="button" variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button type="submit" disabled={saving}>
            {saving ? 'Creating...' : 'Create Staff'}
          </Button>
        </div>
      </form>
    </div>
  );
};

const PromoteInstructorModal = ({ users, onClose, onSuccess }) => {
  const [selectedUserId, setSelectedUserId] = useState('');
  const [role, setRole] = useState('instructor');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const selectedUser = users.find((user) => user.id === selectedUserId) || null;

  const handlePromote = async () => {
    if (!selectedUser) {
      setError('Select a registered user first.');
      return;
    }

    setSaving(true);
    setError('');

    try {
      const timestamp = new Date().toISOString();
      const userPayload = {
        ...selectedUser,
        role,
        updatedAt: timestamp,
        personalDetails: {
          ...(selectedUser.personalDetails || {}),
          firstName: selectedUser.firstName || selectedUser.personalDetails?.firstName || '',
          lastName: selectedUser.lastName || selectedUser.personalDetails?.lastName || '',
          email: selectedUser.email || selectedUser.personalDetails?.email || '',
          status: selectedUser.status || 'active',
        },
      };

      await Promise.all([
        set(ref(db, `users/${selectedUser.id}`), {
          ...userPayload,
          uid: selectedUser.id,
        }),
        canOwnCourses(role)
          ? set(ref(db, `instructors/${selectedUser.id}`), userPayload)
          : Promise.resolve(),
      ]);

      onSuccess();
    } catch (saveError) {
      console.error('Failed to promote user:', saveError);
      setError('Could not upgrade this user to staff.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6">
      <h2 className="text-xl font-semibold text-stone-950">Upgrade Existing User</h2>
      <p className="mt-1 text-sm text-stone-500">
        Select a registered user and assign a staff role.
      </p>

      <div className="mt-5 space-y-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-[minmax(0,1fr)_13rem]">
          <div>
            <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-stone-500">Registered User</label>
            <select
              value={selectedUserId}
              onChange={(event) => setSelectedUserId(event.target.value)}
              className="ledger-input min-h-[38px]"
            >
              <option value="">Select a user</option>
              {users.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.firstName} {user.lastName} - {user.email}
                </option>
              ))}
            </select>
          </div>
          <SelectField
            label="Role"
            value={role}
            onChange={setRole}
            options={STAFF_ROLE_OPTIONS}
          />
        </div>

        {selectedUser ? (
          <div className="rounded-lg border border-stone-200 bg-stone-50 p-4">
            <div className="grid gap-3 sm:grid-cols-3">
              <Metric
                label="Name"
                value={`${selectedUser.firstName || ''} ${selectedUser.lastName || ''}`.trim()}
              />
              <Metric label="Email" value={selectedUser.email || 'No email'} />
              <Metric label="New Role" value={getStaffRoleLabel(role)} />
            </div>
          </div>
        ) : null}

        {error ? <div className="text-sm text-red-600">{error}</div> : null}

        <div className="flex justify-end gap-3 pt-2">
          <Button type="button" variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button type="button" onClick={handlePromote} disabled={saving}>
            {saving ? 'Upgrading...' : 'Upgrade to Staff'}
          </Button>
        </div>
      </div>
    </div>
  );
};

const Field = ({ label, value, onChange, type = 'text' }) => (
  <label className="block">
    <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-stone-500">{label}</span>
    <input
      type={type}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      className="ledger-input min-h-[38px]"
      required
    />
  </label>
);

const SelectField = ({ label, value, onChange, options }) => (
  <label className="block">
    <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-stone-500">{label}</span>
    <select
      value={value}
      onChange={(event) => onChange(event.target.value)}
      className="ledger-input min-h-[38px]"
    >
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  </label>
);

const StaffDetailItem = ({ label, value, className = '' }) => {
  const display =
    value === null || value === undefined || String(value).trim() === ''
      ? 'Not set'
      : String(value);

  return (
    <div className={`px-4 py-3 ${className}`}>
      <p className="text-[10px] font-semibold uppercase tracking-wide text-stone-500">{label}</p>
      <p className="mt-0.5 break-words text-sm font-semibold text-stone-950">{display}</p>
    </div>
  );
};

const StaffAuthFields = ({
  hasLogin,
  password,
  onPasswordChange,
  onGeneratePassword,
}) => (
  <div className="rounded-xl border border-stone-200 bg-stone-50/80 p-4">
    <div className="flex flex-wrap items-center justify-between gap-2">
      <div>
        <p className="text-sm font-semibold text-stone-900">Login credentials</p>
        <p className="mt-1 text-xs leading-5 text-stone-600">
          {hasLogin
            ? 'Leave blank to keep the current password, or enter a new one to reset it.'
            : 'No Firebase login exists yet. Set a password to create sign-in access for this email.'}
        </p>
      </div>
      <WorkspaceStatusChip
        status={hasLogin ? 'active' : 'unassigned'}
        label={hasLogin ? 'Login enabled' : 'No login'}
      />
    </div>
    <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-end">
      <div className="min-w-0 flex-1">
        <StaffFormField
          label={hasLogin ? 'New password (optional)' : 'Set password'}
          type="text"
          value={password}
          onChange={onPasswordChange}
          placeholder={hasLogin ? 'Leave blank to keep current' : 'Minimum 8 characters'}
        />
      </div>
      <Button type="button" variant="outline" onClick={onGeneratePassword}>
        Generate
      </Button>
    </div>
  </div>
);

const StaffFormField = ({
  label,
  value,
  onChange,
  type = 'text',
  required = false,
  placeholder = '',
}) => (
  <label className="block">
    <span className="text-xs font-semibold uppercase tracking-wide text-stone-500">
      {label}
    </span>
    <input
      type={type}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      placeholder={placeholder}
      required={required}
      className="mt-1.5 block w-full rounded-xl border border-stone-200 bg-white px-3 py-2.5 text-sm text-stone-950 shadow-sm focus:border-[#d61f74] focus:outline-none focus:ring-2 focus:ring-[#f4b6d2]"
    />
  </label>
);

const StaffFormSelect = ({ label, value, onChange, options, hint }) => (
  <label className="block">
    <span className="text-xs font-semibold uppercase tracking-wide text-stone-500">
      {label}
    </span>
    <select
      value={value}
      onChange={(event) => onChange(event.target.value)}
      className="mt-1.5 block w-full rounded-xl border border-stone-200 bg-white px-3 py-2.5 text-sm text-stone-950 shadow-sm focus:border-[#d61f74] focus:outline-none focus:ring-2 focus:ring-[#f4b6d2]"
    >
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
    {hint ? <p className="mt-1.5 text-xs leading-5 text-stone-500">{hint}</p> : null}
  </label>
);

const StaffSummaryStat = ({ label, value, className = '' }) => (
  <div className={`bg-white px-4 py-3 ${className}`}>
    <p className="text-[10px] font-semibold uppercase tracking-wide text-stone-500">{label}</p>
    <p className="mt-0.5 text-base font-semibold text-stone-950">{value}</p>
  </div>
);

const SummaryFact = ({ label, value }) => (
  <div className="min-w-0">
    <p className="text-[10px] font-semibold uppercase tracking-wide text-stone-500">{label}</p>
    <p className="mt-0.5 truncate font-semibold text-stone-900">{value}</p>
  </div>
);

const StaffSummaryItem = ({ label, children }) => (
  <div className="min-w-0">
    <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-stone-500">{label}</p>
    {children}
  </div>
);

const Metric = ({ label, value }) => (
  <div className="min-w-0 rounded-lg bg-stone-50 px-3 py-2">
    <p className="truncate text-[10px] font-semibold uppercase tracking-wide text-stone-500">
      {label}
    </p>
    <p className="mt-1 truncate text-sm font-semibold text-stone-950">{value}</p>
  </div>
);

const StaffStat = ({ label, value }) => (
  <div className="rounded-lg border border-stone-200 bg-stone-50 px-3 py-2">
    <p className="text-[10px] font-semibold uppercase tracking-wide text-stone-500">{label}</p>
    <p className="mt-0.5 truncate text-base font-semibold text-stone-950">{value}</p>
  </div>
);

const createInstructorWithSecondaryApp = async (form) => {
  const appName = `instructor-create-${Date.now()}`;
  const secondaryApp = initializeApp(firebaseApp.options, appName);
  const secondaryAuth = getAuth(secondaryApp);

  try {
    const credential = await createUserWithEmailAndPassword(
      secondaryAuth,
      form.email,
      form.password,
    );

    await updateProfile(credential.user, {
      displayName: `${form.firstName} ${form.lastName}`.trim(),
    });

    const timestamp = new Date().toISOString();
    const payload = {
      uid: credential.user.uid,
      firstName: form.firstName,
      lastName: form.lastName,
      email: form.email,
      role: form.role,
      status: form.status,
      authCreated: true,
      authLinkedUid: credential.user.uid,
      authCreatedAt: timestamp,
      createdAt: timestamp,
      updatedAt: timestamp,
      personalDetails: {
        firstName: form.firstName,
        lastName: form.lastName,
        email: form.email,
      },
    };

    await Promise.all([
      set(ref(db, `users/${credential.user.uid}`), payload),
      canOwnCourses(form.role)
        ? set(ref(db, `instructors/${credential.user.uid}`), payload)
        : Promise.resolve(),
    ]);
  } finally {
    await signOut(secondaryAuth).catch(() => {});
    await deleteApp(secondaryApp).catch(() => {});
  }
};

export default InstructorsPage;
